import { Grid, Typography } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import Creatable from 'react-select/creatable';

import { theme } from 'src/styles/theme';

import Caption from '../../../Typography/Caption';
import messages from '../../messages';
import CustomDropdownIndicator from '../CustomDropdownIndicator';
import CustomControl from '../StyledControl';
import { StyledOption } from '../StyledOption';
import useStyles from './styles';

export const defaultStyles = {
  clearIndicator: provided => ({
    ...provided,
    padding: theme.spacing(0.5, 1),
  }),
  container: (provided, state) => {
    if (state.selectProps.inlineSearch) {
      return {
        background: theme.palette.common.white,
        border: theme.customBorders.dropdown,
        borderRadius: 4,
        boxShadow: theme.customShadows.shadow4,
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
      };
    }
    return null;
  },
  input: provided => ({
    ...provided,
    margin: 0,
  }),
  menu: (provided, state) => ({
    ...provided,
    border: !state.selectProps.inlineSearch && theme.customBorders.gray400,
    boxShadow: !state.selectProps.inlineSearch && theme.customShadows.shadow4,
    marginBottom: 0,
    position: state.selectProps.inlineSearch && 'unset',
  }),
  menuList: provided => ({
    ...provided,
    padding: theme.spacing(1),
  }),
  menuPortal: base => ({ ...base, zIndex: 801 }),
  multiValue: provided => ({
    ...provided,
    '&:hover': {
      backgroundColor: theme.palette.common.blue200,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.blue100,
    borderRadius: theme.highlightBorderRadius,
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(0.25),
    marginLeft: 'unset',

    padding: theme.spacing(1, 1.75, 1, 1.25),
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: theme.palette.common.blue600,
    padding: 0,
  }),
  multiValueRemove: provided => ({
    ...provided,
    '&:hover': {
      backgroundColor: theme.palette.common.blue100,
      borderRadius: '50%',
      color: theme.palette.common.blue600,
    },
    '> svg': {
      height: theme.spacing(2),
      width: theme.spacing(2),
    },
    color: theme.palette.common.blue600,

    marginBottom: theme.spacing(-0.125),

    padding: 0,
  }),
  singleValue: provided => ({
    ...provided,
    marginLeft: 0,
    marginRight: theme.spacing(1),
  }),
  valueContainer: provided => ({
    ...provided,
    padding: 0,
  }),
};

const formatGroupLabel = data => (
  <Grid>
    <Typography variant="h5">{data.label}...</Typography>
  </Grid>
);

function Selector({
  className,
  error,
  inlineSearch,
  isCreatable,
  label,
  menuPlacement,
  noOptionsMessage,
  placeholder,
  size,
  components,
  formatOptionLabel,
  required,
  ...otherProps
}) {
  const classes = useStyles();

  const ReactSelect = isCreatable ? Creatable : Select;

  const inlineSearchDerivedProps = inlineSearch
    ? { defaultMenuIsOpen: true, inlineSearch, menuIsOpen: true }
    : { inlineSearch, menuPortalTarget: document.body };

  return (
    <Grid className={classNames(classes.selector, className)}>
      {label && (
        <Typography className={classNames(classes.label, { required })} variant="body2">
          {label}
        </Typography>
      )}
      <ReactSelect
        aria-labelledby={label}
        components={{
          Control: CustomControl,
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: null,
          LoadingIndicator: null,
          Option: StyledOption,
          ...components,
        }}
        error={error}
        formatGroupLabel={formatGroupLabel}
        formatOptionLabel={formatOptionLabel}
        menuPlacement={menuPlacement}
        noOptionsMessage={noOptionsMessage}
        placeholder={placeholder}
        size={size}
        styles={defaultStyles}
        {...inlineSearchDerivedProps}
        {...otherProps}
      />

      {error && !inlineSearch && (
        <Caption align="right" classes={{ root: classes.errorMessage }} color="error">
          {error}
        </Caption>
      )}
    </Grid>
  );
}

Selector.propTypes = {
  className: PropTypes.string,
  components: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  formatOptionLabel: PropTypes.func,
  inlineSearch: PropTypes.bool,
  isCreatable: PropTypes.bool,
  label: PropTypes.node,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  noOptionsMessage: PropTypes.func,
  placeholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  required: PropTypes.bool,
  size: PropTypes.string,
};

Selector.defaultProps = {
  className: null,
  isCreatable: false,
  label: null,
  menuPlacement: 'auto',
  noOptionsMessage: () => <FormattedMessage {...messages.noOptionsMessage} />,
  placeholder: <FormattedMessage {...messages.placeholder} />,
  size: 'medium',
};

export default Selector;
